import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { textFor } from '../localizedText';
import { signalSurface } from '../signalStyles';
import { BlockHeading } from './BlockHeading';
import { readStatementConfig } from './statement';
import { BlockRendererProps, questionIdOf } from './types';

/**
 * `statement@1`: prose the respondent reads. The first CONTENT block, so the first one that renders
 * no control at all.
 *
 * Deliberately inert. It never calls `onChange`, has no interactive element, and takes no focus of
 * its own — the runner's Next button is the only thing to interact with on the screen. That is the
 * whole point: pro-core refuses an answer for a statement, so a renderer that could produce one
 * would only manufacture a submit error.
 *
 * Read as a `<section>` with the question heading as its accessible name where the block has one, so
 * assistive technology announces it as a passage of text rather than as an unlabelled group. Nothing
 * about "you have read this" is claimed or recorded — displaying text proves only that it was
 * displayed (catalog §13).
 *
 * `emphasis` is a bounded toggle, not styling: this component decides what emphasised looks like,
 * and it does so by moving the prose into a hairline surface — an inset panel rather than a tint. The
 * accent is deliberately not used here even though emphasis is exactly where it is tempting: in this
 * flow blue means "this is your answer", and a statement collects none (catalog §12.1).
 */
export const StatementBlock = ({ block, locale }: BlockRendererProps) => {
  const { paragraphs, emphasis } = useMemo(
    () => readStatementConfig(block),
    [block]
  );

  const heading = textFor(block.content?.question, locale);
  const hasWords =
    Boolean(heading) ||
    Boolean(textFor(block.content?.help, locale)) ||
    Boolean(textFor(block.content?.instruction, locale));

  return (
    <Stack
      component="section"
      spacing={2}
      aria-labelledby={heading ? questionIdOf(block) : undefined}
      data-testid={`form-statement-${block.instanceId}`}
    >
      {/*
        Head first, prose second — the order every other block uses, and the order help was NOT in
        here: it used to sit under the paragraphs, where a line meant to frame the passage arrived
        after the passage it was framing.

        `showOptional` is off because requiredness is meaningless on a block that collects nothing —
        pro-core refuses `required: true` on one — so announcing "Optional" would offer a choice
        nobody has. There is no derived hint for the same reason: nothing here is answered.
      */}
      {hasWords && (
        <BlockHeading block={block} locale={locale} showOptional={false} />
      )}

      <Box sx={emphasis ? { ...signalSurface, p: 2 } : undefined}>
        <Stack spacing={1.5}>
          {paragraphs.map(paragraph => (
            <Typography
              key={paragraph.key}
              variant="body1"
              color="text.primary"
              sx={{ whiteSpace: 'pre-line' }}
            >
              {textFor(paragraph.label, locale)}
            </Typography>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
};
